import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import PermissionRoute from './components/PermissionRoute/PermissionRoute';
import LoginPage from './pages/Login/LoginPage';
import ForgotPasswordPage from './pages/ForgotPassword/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPassword/ResetPasswordPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import FormationsPage from './pages/Formations/FormationsPage';
import FormationDetailPage from './pages/Formations/FormationDetailPage';
import FormationEditPage from './pages/Formations/FormationEditPage';
import SessionsPage from './pages/Sessions/SessionsPage';
import SessionDetailPage from './pages/Sessions/SessionDetailPage';
import NewSessionPage from './pages/Sessions/NewSessionPage';
import EditSessionPage from './pages/Sessions/EditSessionPage';
import ConsommablesPage from './pages/Consommables/ConsommablesPage';
import InstructorsPage from './pages/Instructors/InstructorsPage';
import FournisseursPage from './pages/Fournisseurs/FournisseursPage';
import UsersPage from './pages/Users/UsersPage';
import CentresPage from './pages/Centres/CentresPage';
import PermissionsAdminPage from './pages/PermissionsAdmin/PermissionsAdminPage';
import ReportingApiPage from './pages/ReportingApi/ReportingApiPage';
import LogsPage from './pages/Logs/LogsPage';
import ProfilePage from './pages/Profile/ProfilePage';
import DemandesPage from './pages/Demandes/DemandesPage';
import DemandeDetailPage from './pages/Demandes/DemandeDetailPage';
import ComptabilitePage from './pages/Comptabilite/ComptabilitePage';
import SupplierRequestsPage from './pages/SupplierRequests/SupplierRequestsPage';
import ActionItemsPage from './pages/ActionItems/ActionItemsPage';
import ActionItemDetailPage from './pages/ActionItems/ActionItemDetailPage';

const PrivateRoute = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return isAdmin ? children : <Navigate to="/" />;
};

const App = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="app">
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />}
        />
        <Route
          path="/forgot-password"
          element={isAuthenticated ? <Navigate to="/" /> : <ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={isAuthenticated ? <Navigate to="/" /> : <ResetPasswordPage />}
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="formations" element={
            <PermissionRoute resource="formations">
              <FormationsPage />
            </PermissionRoute>
          } />
          <Route path="formations/:id" element={
            <PermissionRoute resource="formations">
              <FormationDetailPage />
            </PermissionRoute>
          } />
          <Route path="formations/:id/edit" element={
            <PermissionRoute resource="formations">
              <FormationEditPage />
            </PermissionRoute>
          } />
          <Route path="sessions" element={
            <PermissionRoute resource="sessions">
              <SessionsPage />
            </PermissionRoute>
          } />
          <Route path="sessions/new" element={
            <PermissionRoute resource="sessions">
              <NewSessionPage />
            </PermissionRoute>
          } />
          <Route path="sessions/:id" element={
            <PermissionRoute resource="sessions">
              <SessionDetailPage />
            </PermissionRoute>
          } />
          <Route path="sessions/:id/edit" element={
            <PermissionRoute resource="sessions">
              <EditSessionPage />
            </PermissionRoute>
          } />
          <Route path="action-items" element={
            <PermissionRoute resource="action_items">
              <ActionItemsPage />
            </PermissionRoute>
          } />
          <Route path="action-items/new" element={
            <PermissionRoute resource="action_items">
              <Navigate to="/action-items?create=1" replace />
            </PermissionRoute>
          } />
          <Route path="action-items/:id" element={
            <PermissionRoute resource="action_items">
              <ActionItemDetailPage />
            </PermissionRoute>
          } />
          <Route path="consommables" element={
            <PermissionRoute resource="consommables">
              <ConsommablesPage />
            </PermissionRoute>
          } />
          <Route path="instructors" element={
            <PermissionRoute resource="instructors">
              <InstructorsPage />
            </PermissionRoute>
          } />
          <Route path="mes-demandes" element={
            <PermissionRoute resource="demandes">
              <DemandesPage />
            </PermissionRoute>
          } />
          <Route path="mes-demandes/:id" element={
            <PermissionRoute resource="demandes">
              <DemandeDetailPage />
            </PermissionRoute>
          } />
          <Route path="demandes-fournisseurs" element={
            <PermissionRoute resource="supplier_requests">
              <SupplierRequestsPage />
            </PermissionRoute>
          } />
          <Route path="fournisseurs" element={
            <PermissionRoute resource="fournisseurs">
              <FournisseursPage />
            </PermissionRoute>
          } />
          <Route path="users" element={
            <PermissionRoute resource="users">
              <UsersPage />
            </PermissionRoute>
          } />
          <Route path="centres" element={
            <PermissionRoute resource="centres">
              <CentresPage />
            </PermissionRoute>
          } />
          <Route path="comptabilite" element={
            <PermissionRoute resource="comptabilite">
              <ComptabilitePage />
            </PermissionRoute>
          } />
          <Route path="reporting-api" element={<AdminRoute><ReportingApiPage /></AdminRoute>} />
          <Route path="profile" element={<ProfilePage />} />
          <Route
            path="logs"
            element={
              <AdminRoute>
                <LogsPage />
              </AdminRoute>
            }
          />
          <Route
            path="permissions"
            element={
              <AdminRoute>
                <PermissionsAdminPage />
              </AdminRoute>
            }
          />
        </Route>
      </Routes>
    </div>
  );
};

export default App;
